import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { TbKey, TbTrash } from 'react-icons/tb';
import SelectOptionsEditor from '../components/SelectOptionsEditor';
import { useAuth } from '../context/AuthContext';
import { DashboardApi } from '../services/api';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import {
  buildInquiryTypePayload,
  createEmptyInquiryAttributeDraft,
  createEmptyInquiryTypeDraft,
  getInquiryAttributeEntryKey,
  getInquiryAttributeTypeLabel,
  INQUIRY_ATTRIBUTE_SOURCE_OPTIONS,
  INQUIRY_ATTRIBUTE_TYPE_OPTIONS,
  normalizeInquiryTypeItem,
  validateInquiryTypeDraft,
} from './inquiryTypeShared';
import './BotsPage.css';
import './BotAssignmentPills.css';
import './InquiryTypesPage.css';

const normalizeBotList = (response) => (
  Array.isArray(response?.bots)
    ? response.bots
    : Array.isArray(response)
      ? response
      : []
);

const normalizeInquiryTypeList = (response) => (
  Array.isArray(response?.items)
    ? response.items.map((item, index) => normalizeInquiryTypeItem(item, index))
    : []
);

export default function InquiryTypesPage() {
  const { selectedCompanyId, selectedCompany, permissions } = useAuth();
  const companyId = selectedCompanyId || selectedCompany?.id || null;
  const canView = Boolean(permissions?.canViewInquiryTypes || permissions?.canViewBots);
  const canEdit = Boolean(permissions?.canManageInquiryTypes || permissions?.canEditBots);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bots, setBots] = useState([]);
  const [inquiryTypes, setInquiryTypes] = useState([]);
  const [error, setError] = useState(null);
  const [expandedInquiryTypes, setExpandedInquiryTypes] = useState({});
  const [expandedAttributes, setExpandedAttributes] = useState({});
  const [confirmingAttributeDelete, setConfirmingAttributeDelete] = useState({});

  const botLookup = useMemo(
    () => new Map(bots.map((bot) => [String(bot.id), bot])),
    [bots],
  );

  const loadPageData = useCallback(async () => {
    if (!companyId) {
      setBots([]);
      setInquiryTypes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await DashboardApi.getCompanyInquiryTypes(companyId, { includeArchived: true });
      setBots(normalizeBotList(response));
      setInquiryTypes(normalizeInquiryTypeList(response));
    } catch (loadError) {
      const message = loadError.message || 'Kunne ikke hente henvendelsestyper.';
      setBots([]);
      setInquiryTypes([]);
      setError(message);
      notifyError(message, { source: 'Henvendelsestyper' });
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadPageData().catch(() => {});
  }, [loadPageData]);

  useEffect(() => {
    setExpandedInquiryTypes({});
    setExpandedAttributes({});
    setConfirmingAttributeDelete({});
  }, [companyId]);

  const toggleInquiryTypeExpanded = useCallback((entryKey) => {
    if (!entryKey) return;
    setExpandedInquiryTypes((prev) => ({ ...prev, [entryKey]: !prev[entryKey] }));
  }, []);

  const handleAddInquiryType = useCallback(() => {
    if (!canEdit) return;
    const nextDraft = createEmptyInquiryTypeDraft();
    setInquiryTypes((prev) => [...prev, nextDraft]);
    setExpandedInquiryTypes((prev) => ({ ...prev, [nextDraft.clientKey]: true }));
  }, [canEdit]);

  const handleInquiryTypeFieldChange = useCallback((entryKey, field, value) => {
    if (!entryKey || !canEdit) return;
    setInquiryTypes((prev) => prev.map((item) => (
      (item.id ? String(item.id) : item.clientKey) === entryKey
        ? { ...item, [field]: value }
        : item
    )));
    setError(null);
  }, [canEdit]);

  const handleAssignedBotToggle = useCallback((entryKey, botId, checked) => {
    if (!entryKey || !botId || !canEdit) return;
    setInquiryTypes((prev) => prev.map((item) => {
      if ((item.id ? String(item.id) : item.clientKey) !== entryKey) {
        return item;
      }
      const currentIds = Array.isArray(item.assignedBotIds) ? item.assignedBotIds : [];
      const nextIds = checked
        ? Array.from(new Set([...currentIds, Number(botId)]))
        : currentIds.filter((entry) => Number(entry) !== Number(botId));
      return { ...item, assignedBotIds: nextIds };
    }));
    setError(null);
  }, [canEdit]);

  const handleInquiryTypeAttributeAdd = useCallback((entryKey) => {
    if (!entryKey || !canEdit) return;
    const nextAttribute = createEmptyInquiryAttributeDraft();
    const attributeKey = getInquiryAttributeEntryKey(nextAttribute, 0);
    setInquiryTypes((prev) => prev.map((item) => (
      (item.id ? String(item.id) : item.clientKey) === entryKey
        ? { ...item, attributes: [...(item.attributes || []), nextAttribute] }
        : item
    )));
    setExpandedAttributes((prev) => ({
      ...prev,
      [entryKey]: {
        ...(prev[entryKey] || {}),
        [attributeKey]: true,
      },
    }));
    setConfirmingAttributeDelete((prev) => ({ ...prev, [entryKey]: null }));
  }, [canEdit]);

  const toggleInquiryTypeAttributeExpanded = useCallback((entryKey, attributeKey) => {
    if (!entryKey || !attributeKey) return;
    setExpandedAttributes((prev) => ({
      ...prev,
      [entryKey]: {
        ...(prev[entryKey] || {}),
        [attributeKey]: !(prev[entryKey] || {})[attributeKey],
      },
    }));
  }, []);

  const handleInquiryTypeAttributeChange = useCallback((entryKey, attributeIndex, field, value) => {
    if (!entryKey || !canEdit) return;
    setInquiryTypes((prev) => prev.map((item) => {
      if ((item.id ? String(item.id) : item.clientKey) !== entryKey) {
        return item;
      }

      const nextAttributes = (item.attributes || []).map((attribute, index) => {
        if (field === 'useAsDisplayName') {
          return {
            ...attribute,
            useAsDisplayName: value === true && index === attributeIndex,
          };
        }
        if (index !== attributeIndex) return attribute;
        const nextAttribute = { ...attribute, [field]: value };
        if (field === 'sourceType') {
          if (value === 'products') {
            if (nextAttribute.type !== 'product') {
              nextAttribute.type = 'select';
            }
            nextAttribute.options = [];
            nextAttribute.sourceValueMode = nextAttribute.sourceValueMode || 'product_id';
          } else {
            nextAttribute.sourceValueMode = 'product_id';
          }
        }
        if (field === 'type' && value === 'product') {
          nextAttribute.sourceType = 'products';
          nextAttribute.sourceValueMode = 'product_id';
          nextAttribute.options = [];
          nextAttribute.defaultValue = '';
          nextAttribute.showInInboxList = false;
          nextAttribute.useAsDisplayName = false;
        }
        if (field === 'type' && value !== 'select') {
          nextAttribute.options = [];
        }
        if (field === 'type' && value !== 'product' && nextAttribute.sourceType === 'products' && value !== 'select') {
          nextAttribute.sourceType = 'none';
          nextAttribute.sourceValueMode = 'product_id';
        }
        if (field === 'collectInChat' && value !== true) {
          nextAttribute.required = false;
        }
        return nextAttribute;
      });

      return { ...item, attributes: nextAttributes };
    }));
    setError(null);
    setConfirmingAttributeDelete((prev) => ({ ...prev, [entryKey]: null }));
  }, [canEdit]);

  const handleInquiryTypeAttributeRemove = useCallback((entryKey, attributeIndex) => {
    if (!entryKey || !canEdit) return;
    let removedAttributeKey = null;
    setInquiryTypes((prev) => prev.map((item) => {
      if ((item.id ? String(item.id) : item.clientKey) !== entryKey) {
        return item;
      }
      const attributes = item.attributes || [];
      removedAttributeKey = getInquiryAttributeEntryKey(attributes[attributeIndex], attributeIndex);
      return { ...item, attributes: attributes.filter((_attribute, index) => index !== attributeIndex) };
    }));
    setExpandedAttributes((prev) => {
      const nextEntryState = { ...(prev[entryKey] || {}) };
      if (removedAttributeKey) {
        delete nextEntryState[removedAttributeKey];
      }
      return { ...prev, [entryKey]: nextEntryState };
    });
    setConfirmingAttributeDelete((prev) => ({ ...prev, [entryKey]: null }));
  }, [canEdit]);

  const handleInquiryTypeAttributeDeleteClick = useCallback((entryKey, attributeIndex, attributeKey) => {
    if (!entryKey || !attributeKey || !canEdit) return;
    if (confirmingAttributeDelete[entryKey] === attributeKey) {
      handleInquiryTypeAttributeRemove(entryKey, attributeIndex);
      return;
    }
    setConfirmingAttributeDelete((prev) => ({ ...prev, [entryKey]: attributeKey }));
  }, [canEdit, confirmingAttributeDelete, handleInquiryTypeAttributeRemove]);

  const handleInquiryTypeAttributeMove = useCallback((entryKey, attributeIndex, direction) => {
    if (!entryKey || !canEdit) return;
    setInquiryTypes((prev) => prev.map((item) => {
      if ((item.id ? String(item.id) : item.clientKey) !== entryKey) {
        return item;
      }

      const attributes = [...(item.attributes || [])];
      const nextIndex = direction === 'up' ? attributeIndex - 1 : attributeIndex + 1;
      if (attributeIndex < 0 || nextIndex < 0 || attributeIndex >= attributes.length || nextIndex >= attributes.length) {
        return item;
      }
      const [moved] = attributes.splice(attributeIndex, 1);
      attributes.splice(nextIndex, 0, moved);
      return { ...item, attributes };
    }));
    setConfirmingAttributeDelete((prev) => ({ ...prev, [entryKey]: null }));
  }, [canEdit]);

  const handleInquiryTypeSave = useCallback(async (draft) => {
    if (!companyId || !draft || !canEdit) return;
    const validationError = validateInquiryTypeDraft(draft);
    if (validationError) {
      setError(validationError);
      notifyError(validationError, { source: 'Henvendelsestyper' });
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = buildInquiryTypePayload(draft);
      await (
        draft.id
          ? DashboardApi.updateCompanyInquiryType(companyId, draft.id, payload)
          : DashboardApi.createCompanyInquiryType(companyId, payload)
      );
      notifySuccess('Henvendelsestypen er lagret.', { source: 'Henvendelsestyper' });
      await loadPageData();
    } catch (saveError) {
      const message = saveError.message || 'Kunne ikke lagre henvendelsestypen.';
      setError(message);
      notifyError(message, { source: 'Henvendelsestyper' });
    } finally {
      setSaving(false);
    }
  }, [canEdit, companyId, loadPageData]);

  const handleInquiryTypeDelete = useCallback(async (draft) => {
    if (!companyId || !draft || !canEdit) return;
    const entryKey = draft.id ? String(draft.id) : draft.clientKey;
    if (!draft.id) {
      setInquiryTypes((prev) => prev.filter((item) => (item.id ? String(item.id) : item.clientKey) !== entryKey));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await DashboardApi.deleteCompanyInquiryType(companyId, draft.id);
      notifySuccess('Henvendelsestypen er arkivert.', { source: 'Henvendelsestyper' });
      await loadPageData();
    } catch (deleteError) {
      const message = deleteError.message || 'Kunne ikke arkivere henvendelsestypen.';
      setError(message);
      notifyError(message, { source: 'Henvendelsestyper' });
    } finally {
      setSaving(false);
    }
  }, [canEdit, companyId, loadPageData]);

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="page inquiry-types-page">
      <div className="page-header inquiry-types-page-header">
        <div>
          <h1>Henvendelsestyper</h1>
          <p className="page-subtitle">
            En henvendelsestype beskriver hvilken type sak kunden har og hvilke felter som skal samles inn i chat, skjema og inbox.
          </p>
        </div>
        <div className="inquiry-types-page-actions">
          <button
            type="button"
            className="button button-secondary"
            onClick={() => loadPageData()}
            disabled={loading || saving}
          >
            Oppdater
          </button>
          <button
            type="button"
            className="button button-primary"
            onClick={handleAddInquiryType}
            disabled={!canEdit || saving}
          >
            Ny henvendelsestype
          </button>
        </div>
      </div>

      {!loading && bots.length === 0 ? (
        <section className="inquiry-types-empty">
          <p>Ingen bots er tilgjengelige for dette selskapet.</p>
          <p className="input-hint">Opprett en bot først for å konfigurere henvendelsestyper.</p>
        </section>
      ) : null}

      <section className="bot-section bot-section--inquiries">
        {error ? (
          <div className="bot-section-alert bot-section-alert--error">
            ⚠️ {error}
          </div>
        ) : null}

        {loading && inquiryTypes.length === 0 ? (
          <p className="input-hint">Laster henvendelsestyper…</p>
        ) : inquiryTypes.length === 0 ? (
          <div className="product-inbox-config-empty">
            <p>Ingen henvendelsestyper er konfigurert ennå.</p>
            <p className="input-hint">Start med en basisstruktur og tildel den til de botene som skal bruke den.</p>
          </div>
        ) : (
          <div className="inquiry-type-list">
            {inquiryTypes.map((inquiryType, inquiryTypeIndex) => {
              const entryKey = inquiryType.id ? String(inquiryType.id) : inquiryType.clientKey;
              const isExpanded = Boolean(expandedInquiryTypes[entryKey]);
              const productSelectorAttributes = (inquiryType.attributes || []).filter((field) => field.type === 'product').length;
              const productBackedAttributes = (inquiryType.attributes || []).filter((field) => field.sourceType === 'products' && field.type !== 'product').length;
              const assignedBotLabels = (inquiryType.assignedBotIds || [])
                .map((botId) => botLookup.get(String(botId)))
                .filter(Boolean)
                .map((bot) => bot.display_title || bot.name);

              return (
                <article key={entryKey} className="inquiry-type-card">
                  <header className="inquiry-type-card-head">
                    <div>
                      <div className="inquiry-type-card-title-row">
                        <h4>{inquiryType.name || `Ny henvendelsestype ${inquiryTypeIndex + 1}`}</h4>
                        <span className="qb-source-badge">
                          {assignedBotLabels.length} {assignedBotLabels.length === 1 ? 'bot' : 'boter'}
                        </span>
                        {productSelectorAttributes > 0 ? (
                          <span className="qb-source-badge">Produkt valg</span>
                        ) : null}
                        {productBackedAttributes > 0 ? (
                          <span className="qb-source-badge">{productBackedAttributes} produktkoblet</span>
                        ) : null}
                      </div>
                      <p className="input-hint">
                        {inquiryType.description || 'Beskriv når denne henvendelsestypen skal brukes og hva den skal samle inn.'}
                      </p>
                      {assignedBotLabels.length > 0 ? (
                        <p className="input-hint inquiry-types-assigned-summary">
                          Tildelt til: {assignedBotLabels.join(', ')}
                        </p>
                      ) : (
                        <p className="input-hint inquiry-types-assigned-summary">
                          Ingen boter valgt ennå.
                        </p>
                      )}
                    </div>
                    <div className="inquiry-type-card-actions">
                      <button
                        type="button"
                        className="button button-secondary button-small"
                        onClick={() => toggleInquiryTypeExpanded(entryKey)}
                      >
                        {isExpanded ? 'Skjul' : 'Konfigurer'}
                      </button>
                      <button
                        type="button"
                        className="button button-primary button-small"
                        onClick={() => handleInquiryTypeSave(inquiryType)}
                        disabled={!canEdit || saving}
                      >
                        {saving ? 'Lagrer…' : 'Lagre'}
                      </button>
                      <button
                        type="button"
                        className="button button-danger button-small"
                        onClick={() => handleInquiryTypeDelete(inquiryType)}
                        disabled={!canEdit || saving}
                      >
                        {inquiryType.id ? 'Arkiver' : 'Fjern'}
                      </button>
                    </div>
                  </header>

                  {isExpanded ? (
                    <div className="inquiry-type-card-body">
                      <div className="bot-grid bot-grid--two">
                        <label>
                          Navn
                          <input
                            className="input"
                            type="text"
                            value={inquiryType.name}
                            onChange={(event) => handleInquiryTypeFieldChange(entryKey, 'name', event.target.value)}
                            disabled={!canEdit || saving}
                            placeholder="f.eks. Salgshenvendelse"
                          />
                        </label>
                        <label style={{ gridColumn: '1 / -1' }}>
                          Routing / når skal denne brukes?
                          <textarea
                            className="textarea"
                            rows={3}
                            value={inquiryType.description}
                            onChange={(event) => handleInquiryTypeFieldChange(entryKey, 'description', event.target.value)}
                            disabled={!canEdit || saving}
                            placeholder="Beskriv hvilke intensjoner eller behov som skal route til denne henvendelsestypen."
                          />
                        </label>
                      </div>

                      <div className="product-inbox-config-header">
                        <div>
                          <h5>Tildelte boter</h5>
                          <p className="input-hint">
                            Velg hvilke boter som skal få denne henvendelsestypen tilgjengelig i chat, inbox og skjema.
                          </p>
                        </div>
                      </div>

                      <div className="bot-assignment-list inquiry-types-assignment-grid">
                        {bots.map((bot) => {
                          const checked = (inquiryType.assignedBotIds || []).some((entry) => Number(entry) === Number(bot.id));
                          return (
                            <button
                              key={bot.id}
                              type="button"
                              className={`bot-assignment-pill inquiry-types-assignment-option${checked ? ' is-selected' : ''}`}
                              onClick={() => handleAssignedBotToggle(entryKey, bot.id, !checked)}
                              disabled={!canEdit || saving}
                              aria-pressed={checked}
                            >
                              <span className="bot-assignment-pill-main">
                                <strong>{bot.display_title || bot.name}</strong>
                                <small>{bot.status || 'active'}</small>
                              </span>
                              <span className="bot-assignment-pill-state">{checked ? 'Tildelt' : 'Ikke tildelt'}</span>
                            </button>
                          );
                        })}
                      </div>
                      <div className="product-inbox-config-header">
                        <div>
                          <h5>Basisattributter</h5>
                          <p className="input-hint">
                            Attributter kan samles inn i chatten, være interne i inboxen, eller kobles til produktkatalogen.
                          </p>
                        </div>
                        <button
                          type="button"
                          className="button button-secondary button-small"
                          onClick={() => handleInquiryTypeAttributeAdd(entryKey)}
                          disabled={!canEdit || saving}
                        >
                          + Attributt
                        </button>
                      </div>

                      {(inquiryType.attributes || []).length === 0 ? (
                        <div className="product-inbox-config-empty">
                          <p>Ingen attributter lagt til ennå.</p>
                        </div>
                      ) : (
                        <div className="product-inbox-config-list">
                          {(inquiryType.attributes || []).map((field, fieldIndex) => {
                            const productSelectorSelected = field.type === 'product';
                            const productSourceSelected = field.sourceType === 'products';
                            const attributeKey = getInquiryAttributeEntryKey(field, fieldIndex);
                            const isAttributeExpanded = Boolean(expandedAttributes[entryKey]?.[attributeKey]);
                            const confirmingDelete = confirmingAttributeDelete[entryKey] === attributeKey;
                            const attributeLabel = String(field.label || '').trim() || `Attributt ${fieldIndex + 1}`;
                            const typeLabel = productSelectorSelected
                              ? 'Produkt valg'
                              : productSourceSelected
                              ? 'Produktkoblet valgliste'
                              : getInquiryAttributeTypeLabel(field.type);

                            return (
                              <div
                                key={attributeKey}
                                className={`product-inbox-config-row inquiry-attribute-row${isAttributeExpanded ? ' is-expanded' : ' is-collapsed'}`}
                              >
                                <header className="inquiry-attribute-summary">
                                  <div className="inquiry-attribute-summary-main">
                                    <div className="inquiry-attribute-title-line">
                                      <strong>{attributeLabel}</strong>
                                      {field.useAsDisplayName === true ? (
                                        <span className="inquiry-attribute-display-name-badge">
                                          <TbKey aria-hidden="true" focusable="false" />
                                          Visningsnavn
                                        </span>
                                      ) : null}
                                    </div>
                                    <span>{typeLabel}</span>
                                  </div>
                                  <div className="inquiry-attribute-summary-actions">
                                    <button
                                      type="button"
                                      className="button button-secondary button-small inquiry-attribute-icon-button"
                                      onClick={() => handleInquiryTypeAttributeMove(entryKey, fieldIndex, 'up')}
                                      disabled={!canEdit || saving || fieldIndex === 0}
                                      aria-label={`Flytt ${attributeLabel} opp`}
                                    >
                                      ↑
                                    </button>
                                    <button
                                      type="button"
                                      className="button button-secondary button-small inquiry-attribute-icon-button"
                                      onClick={() => handleInquiryTypeAttributeMove(entryKey, fieldIndex, 'down')}
                                      disabled={!canEdit || saving || fieldIndex === (inquiryType.attributes || []).length - 1}
                                      aria-label={`Flytt ${attributeLabel} ned`}
                                    >
                                      ↓
                                    </button>
                                    <button
                                      type="button"
                                      className="button button-secondary button-small"
                                      onClick={() => toggleInquiryTypeAttributeExpanded(entryKey, attributeKey)}
                                      disabled={saving}
                                      aria-expanded={isAttributeExpanded}
                                    >
                                      {isAttributeExpanded ? 'Lukk' : 'Utvid'}
                                    </button>
                                    <button
                                      type="button"
                                      className={`button button-small ${confirmingDelete ? 'button-danger' : 'button-secondary'} inquiry-attribute-delete-button`}
                                      onClick={() => handleInquiryTypeAttributeDeleteClick(entryKey, fieldIndex, attributeKey)}
                                      disabled={!canEdit || saving}
                                      aria-label={confirmingDelete ? `Bekreft sletting av ${attributeLabel}` : `Slett ${attributeLabel}`}
                                    >
                                      {confirmingDelete ? 'Bekreft' : <TbTrash aria-hidden="true" focusable="false" />}
                                    </button>
                                  </div>
                                </header>

                                {isAttributeExpanded ? (
                                  <>
                                    <div className="bot-grid bot-grid--two inquiry-attribute-fields">
                                      <label>
                                        Etikett
                                        <input
                                          className="input"
                                          type="text"
                                          value={field.label}
                                          onChange={(event) => handleInquiryTypeAttributeChange(entryKey, fieldIndex, 'label', event.target.value)}
                                          disabled={!canEdit || saving}
                                          placeholder="f.eks. Produkt"
                                        />
                                      </label>
                                      <label>
                                        Felttype
                                        <select
                                          className="select"
                                          value={field.type}
                                          onChange={(event) => handleInquiryTypeAttributeChange(entryKey, fieldIndex, 'type', event.target.value)}
                                          disabled={!canEdit || saving}
                                        >
                                          {INQUIRY_ATTRIBUTE_TYPE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                              {option.label}
                                            </option>
                                          ))}
                                        </select>
                                      </label>
                                      <label>
                                        Datakilde
                                        <select
                                          className="select"
                                          value={productSelectorSelected ? 'products' : field.sourceType}
                                          onChange={(event) => handleInquiryTypeAttributeChange(entryKey, fieldIndex, 'sourceType', event.target.value)}
                                          disabled={!canEdit || saving || productSelectorSelected}
                                        >
                                          {INQUIRY_ATTRIBUTE_SOURCE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                              {option.label}
                                            </option>
                                          ))}
                                        </select>
                                      </label>

                                      {productSelectorSelected ? (
                                        <label>
                                          Produktverdi
                                          <input
                                            className="input"
                                            type="text"
                                            value="Produkt-ID"
                                            disabled
                                            readOnly
                                          />
                                          <span className="input-hint">
                                            Feltet velger produkt for henvendelsen og vises ikke som et eget attributt i inboxen.
                                          </span>
                                        </label>
                                      ) : productSourceSelected ? (
                                        <label>
                                          Produktverdi
                                          <select
                                            className="select"
                                            value={field.sourceValueMode}
                                            onChange={(event) => handleInquiryTypeAttributeChange(entryKey, fieldIndex, 'sourceValueMode', event.target.value)}
                                            disabled={!canEdit || saving}
                                          >
                                            <option value="product_id">Produkt-ID</option>
                                            <option value="product_name">Produktnavn</option>
                                          </select>
                                          <span className="input-hint">
                                            Produktlisten hentes fra hver bot som er tildelt denne henvendelsestypen.
                                          </span>
                                        </label>
                                      ) : (
                                        <label>
                                          Standardverdi
                                          {field.type === 'boolean' ? (
                                            <select
                                              className="select"
                                              value={String(field.defaultValue ?? '')}
                                              onChange={(event) => handleInquiryTypeAttributeChange(entryKey, fieldIndex, 'defaultValue', event.target.value)}
                                              disabled={!canEdit || saving}
                                            >
                                              <option value="">Ingen</option>
                                              <option value="true">Ja</option>
                                              <option value="false">Nei</option>
                                            </select>
                                          ) : (
                                            <input
                                              className="input"
                                              type="text"
                                              value={field.defaultValue ?? ''}
                                              onChange={(event) => handleInquiryTypeAttributeChange(entryKey, fieldIndex, 'defaultValue', event.target.value)}
                                              disabled={!canEdit || saving}
                                              placeholder="Valgfri standardverdi"
                                            />
                                          )}
                                        </label>
                                      )}

                                      <label style={{ gridColumn: '1 / -1' }}>
                                        Hjelpetekst
                                        <input
                                          className="input"
                                          type="text"
                                          value={field.helpText}
                                          onChange={(event) => handleInquiryTypeAttributeChange(entryKey, fieldIndex, 'helpText', event.target.value)}
                                          disabled={!canEdit || saving}
                                          placeholder="Kort forklaring til saksbehandler eller kunde"
                                        />
                                      </label>
                                      <label style={{ gridColumn: '1 / -1' }}>
                                        Placeholder
                                        <input
                                          className="input"
                                          type="text"
                                          value={field.placeholder}
                                          onChange={(event) => handleInquiryTypeAttributeChange(entryKey, fieldIndex, 'placeholder', event.target.value)}
                                          disabled={!canEdit || saving}
                                          placeholder="Tekst som vises i tomt felt"
                                        />
                                      </label>

                                      {!productSourceSelected && field.type === 'select' ? (
                                        <div className="label" style={{ gridColumn: '1 / -1' }}>
                                          <span className="label-text">Valg</span>
                                          <SelectOptionsEditor
                                            options={field.options}
                                            onChange={(nextOptions) =>
                                              handleInquiryTypeAttributeChange(entryKey, fieldIndex, 'options', nextOptions)}
                                            disabled={!canEdit || saving}
                                          />
                                        </div>
                                      ) : null}

                                      <label className="checkbox-field">
                                        <input
                                          type="checkbox"
                                          checked={field.collectInChat === true}
                                          onChange={(event) => handleInquiryTypeAttributeChange(entryKey, fieldIndex, 'collectInChat', event.target.checked)}
                                          disabled={!canEdit || saving}
                                        />
                                        <span>Samles inn i chat</span>
                                      </label>
                                      <label className="checkbox-field">
                                        <input
                                          type="checkbox"
                                          checked={field.required === true}
                                          onChange={(event) => handleInquiryTypeAttributeChange(entryKey, fieldIndex, 'required', event.target.checked)}
                                          disabled={!canEdit || saving || field.collectInChat !== true}
                                        />
                                        <span>Påkrevd</span>
                                      </label>
                                      <label className="checkbox-field">
                                        <input
                                          type="checkbox"
                                          checked={field.showInInboxList === true}
                                          onChange={(event) => handleInquiryTypeAttributeChange(entryKey, fieldIndex, 'showInInboxList', event.target.checked)}
                                          disabled={!canEdit || saving || productSelectorSelected}
                                        />
                                        <span>Vis i inboxlisten</span>
                                      </label>
                                      <label className="checkbox-field">
                                        <input
                                          type="checkbox"
                                          checked={field.useAsDisplayName === true}
                                          onChange={(event) => handleInquiryTypeAttributeChange(entryKey, fieldIndex, 'useAsDisplayName', event.target.checked)}
                                          disabled={!canEdit || saving || productSelectorSelected}
                                        />
                                        <span>Visningsnavn</span>
                                      </label>
                                    </div>
                                  </>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
